import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FacebookLogo, PaperPlaneTilt } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";

export default function FacebookCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();

  const load = () =>
    api
      .get("/integrations/facebook/status")
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ connected: false }));

  useEffect(() => {
    load();
    const fb = params.get("fb");
    if (fb === "connected") toast.success("Facebook page connected! 🎉");
    else if (fb === "no_pages")
      toast.error("Facebook didn't share any Page. Reconnect and pick your page when Facebook asks.", { duration: 8000 });
    else if (fb) toast.error("Facebook connection failed — try again.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/integrations/facebook/connect");
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't start Facebook connect.");
      setBusy(false);
    }
  };

  const selectPage = async (page_id) => {
    await api.post("/integrations/facebook/select-page", { page_id });
    load();
  };

  const toggleAutopost = async (enabled) => {
    try {
      await api.post("/integrations/facebook/autopost", { enabled });
      setStatus((s) => ({ ...s, autopost: enabled }));
      toast.success(enabled ? "Auto-posting on — new stops will post to your page." : "Auto-posting off.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't update.");
    }
  };

  const testPost = async () => {
    setBusy(true);
    try {
      await api.post("/integrations/facebook/test-post", {
        message: "We're live on TruckTrackr — track us in real time! 🚚 https://thetrucktrackr.com",
      });
      toast.success("Test post published to your page!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Post failed.");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    await api.post("/integrations/facebook/disconnect");
    setStatus({ connected: false });
    toast.success("Facebook disconnected.");
  };

  if (!status) return null;

  return (
    <div className="rounded-2xl border bg-card p-5 space-y-3" data-testid="facebook-card">
      <div className="flex items-center gap-2">
        <FacebookLogo weight="fill" className="h-5 w-5 text-[#1877F2]" />
        <h3 className="font-semibold text-sm">Facebook Page</h3>
        {!status.connected && !status.available && (
          <span className="text-[10px] font-bold uppercase tracking-wider bg-[#1877F2]/10 text-[#1877F2] rounded-full px-2 py-0.5">Coming soon</span>
        )}
        {status.connected && (
          <button onClick={disconnect} className="ml-auto text-[11px] text-muted-foreground underline" data-testid="fb-disconnect-btn">
            disconnect
          </button>
        )}
      </div>
      {!status.connected && !status.available ? (
        <p className="text-sm text-muted-foreground" data-testid="fb-coming-soon">
          Auto-post your new stops to your Facebook Page and import your schedule straight from your posts — we're waiting on Facebook's approval. Hang tight!
        </p>
      ) : !status.connected ? (
        <>
          <p className="text-sm text-muted-foreground">
            Connect your page to auto-post new stops and import your schedule straight from your Facebook posts.
          </p>
          <Button size="sm" onClick={connect} disabled={busy} className="bg-[#1877F2] hover:bg-[#1465cc] text-white" data-testid="fb-connect-btn">
            <FacebookLogo weight="fill" className="h-4 w-4 mr-1.5" /> Connect Facebook Page
          </Button>
        </>
      ) : !status.pages?.length ? (
        <>
          <p className="text-sm text-amber-600">
            Connected, but no Page was shared. Reconnect and make sure to select your page when Facebook asks which Pages to allow.
          </p>
          <Button size="sm" onClick={connect} disabled={busy} className="bg-[#1877F2] hover:bg-[#1465cc] text-white" data-testid="fb-reconnect-btn">
            <FacebookLogo weight="fill" className="h-4 w-4 mr-1.5" /> Reconnect
          </Button>
        </>
      ) : (
        <>
          {status.pages?.length > 1 && (
            <select
              value={status.page_id || ""}
              onChange={(e) => selectPage(e.target.value)}
              className="w-full h-9 rounded-lg border bg-background px-2 text-sm"
              data-testid="fb-page-select"
            >
              <option value="" disabled>Pick a page…</option>
              {status.pages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          {status.pages?.length === 1 && (
            <p className="text-sm font-medium">{status.pages[0].name}</p>
          )}
          <div className="flex items-center justify-between">
            <span className="text-sm">Auto-post new stops to Facebook</span>
            <Switch checked={!!status.autopost} onCheckedChange={toggleAutopost} data-testid="fb-autopost-toggle" />
          </div>
          <Button size="sm" variant="outline" onClick={testPost} disabled={busy || !status.page_id} data-testid="fb-test-post-btn">
            <PaperPlaneTilt className="h-4 w-4 mr-1.5" /> Send a test post
          </Button>
        </>
      )}
    </div>
  );
}
